import React, { useState } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Avatar,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    IconButton,
    LinearProgress,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Breadcrumbs,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    CreateNewFolder as FolderIcon,
    FileCopy as FileIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    Visibility as ViewIcon,
    Storage as StorageIcon,
    Folder as FolderOpenIcon,
    InsertDriveFile as InsertDriveFileIcon,
    Image as ImageIcon,
    Code as CodeIcon,
    TableChart as TableChartIcon,
    Archive as ArchiveIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';

// Import mock data
import { storageData, projectsData } from '../../dummy_data/datahubMockData';

const StorageManagement = () => {
    const [selectedTab, setSelectedTab] = useState(0);
    const [currentPath, setCurrentPath] = useState('/');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const [openCreateFolderDialog, setOpenCreateFolderDialog] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [sortBy, setSortBy] = useState('name');

    const getFileIcon = (fileType) => {
        switch (fileType) {
            case 'csv':
            case 'xlsx':
            case 'json':
                return <TableChartIcon />;
            case 'jpg':
            case 'png':
            case 'gif':
                return <ImageIcon />;
            case 'py':
            case 'ipynb':
            case 'js':
            case 'ts':
                return <CodeIcon />;
            case 'zip':
            case 'tar':
            case 'gz':
                return <ArchiveIcon />;
            default:
                return <InsertDriveFileIcon />;
        }
    };

    const getFileColor = (fileType) => {
        switch (fileType) {
            case 'csv':
            case 'xlsx':
            case 'json':
                return 'success';
            case 'jpg':
            case 'png':
            case 'gif':
                return 'info';
            case 'py':
            case 'ipynb':
            case 'js':
            case 'ts':
                return 'warning';
            case 'zip':
            case 'tar':
            case 'gz':
                return 'secondary';
            default:
                return 'default';
        }
    };

    const formatFileSize = (size) => {
        const sizes = ['B', 'KB', 'MB', 'GB'];
        let i = 0;
        let fileSize = parseFloat(size);
        while (fileSize >= 1024 && i < sizes.length - 1) {
            fileSize /= 1024;
            i++;
        }
        return `${fileSize.toFixed(1)} ${sizes[i]}`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    const handleFileSelect = (file) => {
        if (selectedFiles.find((f) => f.id === file.id)) {
            setSelectedFiles(selectedFiles.filter((f) => f.id !== file.id));
        } else {
            setSelectedFiles([...selectedFiles, file]);
        }
    };

    const handleCreateFolder = () => {
        console.log('Creating folder:', newFolderName);
        setOpenCreateFolderDialog(false);
        setNewFolderName('');
    };

    const handleUpload = () => {
        console.log('Uploading files...');
        setOpenUploadDialog(false);
    };

    const TabPanel = ({ children, value, index }) => (
        <div hidden={value !== index} style={{ paddingTop: '20px' }}>
            {value === index && children}
        </div>
    );

    return (
        <Box sx={{ flexGrow: 1, p: 2 }}>
            {/* Compact Header */}
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                        Storage Management
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                            variant="outlined" 
                            size="small"
                            startIcon={<FolderIcon />} 
                            onClick={() => setOpenCreateFolderDialog(true)}
                        >
                            New Folder
                        </Button>
                        <Button 
                            variant="contained" 
                            size="small"
                            startIcon={<UploadIcon />} 
                            onClick={() => setOpenUploadDialog(true)}
                        >
                            Upload
                        </Button>
                    </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    Manage your data files, organize storage, and monitor usage
                </Typography>
            </Box>

            {/* Compact Storage Overview */}
            <Card sx={{ mb: 3 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <StorageIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                                <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                        {storageData.overview.totalUsed}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                        Used
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FolderOpenIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
                                <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                        {storageData.overview.folders}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                        Folders
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FileIcon sx={{ fontSize: 20, color: 'success.main' }} />
                                <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                        {storageData.overview.files}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                        Files
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                            Usage
                                        </Typography>
                                        <Typography variant="caption" fontWeight="medium" sx={{ fontSize: '0.7rem' }}>
                                            {storageData.overview.percentage}%
                                        </Typography>
                                    </Box>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={storageData.overview.percentage} 
                                        sx={{ height: 4, borderRadius: 1 }}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Compact Storage by Project & Backends - Collapsible */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, fontSize: '0.875rem', fontWeight: 600 }}>
                                Storage by Project
                            </Typography>
                            <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                                {storageData.byProject.map((project, index) => (
                                    <Box key={project.projectId} sx={{ mb: index < storageData.byProject.length - 1 ? 1 : 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                                                {project.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                {Math.round((parseFloat(project.used) / 10) * 100)}%
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={(parseFloat(project.used) / 10) * 100}
                                                sx={{ flex: 1, height: 4, borderRadius: 1 }}
                                            />
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', minWidth: '60px', textAlign: 'right' }}>
                                                {project.files} files
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, fontSize: '0.875rem', fontWeight: 600 }}>
                                Storage Backends
                            </Typography>
                            <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                                {storageData.storageBackends.map((backend, index) => (
                                    <Box 
                                        key={backend.name} 
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'space-between',
                                            mb: index < storageData.storageBackends.length - 1 ? 1.5 : 0,
                                            pb: index < storageData.storageBackends.length - 1 ? 1.5 : 0,
                                            borderBottom: index < storageData.storageBackends.length - 1 ? '1px solid' : 'none',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <StorageIcon sx={{ fontSize: 18, color: 'success.main' }} />
                                            <Box>
                                                <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                                                    {backend.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                    {backend.used} / {backend.total}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Chip label={backend.status} size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                                    </Box>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* File Browser */}
            <Card>
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Tabs 
                        value={selectedTab} 
                        onChange={(e, newValue) => setSelectedTab(newValue)}
                        sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                    >
                        <Tab label="File Browser" sx={{ textTransform: 'none', fontSize: '0.875rem', minHeight: 48 }} />
                        <Tab label="Recent Files" sx={{ textTransform: 'none', fontSize: '0.875rem', minHeight: 48 }} />
                        <Tab label="Analytics" sx={{ textTransform: 'none', fontSize: '0.875rem', minHeight: 48 }} />
                    </Tabs>
                    <Box sx={{ p: 2 }}>

                    <TabPanel value={selectedTab} index={0}>
                        {/* Compact Breadcrumb Navigation */}
                        <Box sx={{ mb: 1.5 }}>
                            <Breadcrumbs separator="›" sx={{ fontSize: '0.8rem' }}>
                                <Link color="inherit" href="#" onClick={() => setCurrentPath('/')} sx={{ fontSize: '0.8rem' }}>
                                    Home
                                </Link>
                                {currentPath
                                    .split('/')
                                    .filter(Boolean)
                                    .map((segment, index) => (
                                        <Link
                                            key={index}
                                            color="inherit"
                                            href="#"
                                            onClick={() =>
                                                setCurrentPath(
                                                    '/' +
                                                        currentPath
                                                            .split('/')
                                                            .slice(1, index + 2)
                                                            .join('/')
                                                )
                                            }
                                            sx={{ fontSize: '0.8rem' }}
                                        >
                                            {segment}
                                        </Link>
                                    ))}
                            </Breadcrumbs>
                        </Box>

                        {/* Compact Search and Filter */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                            <TextField
                                size="small"
                                placeholder="Search files..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ flexGrow: 1 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                                <InputLabel sx={{ fontSize: '0.8rem' }}>Type</InputLabel>
                                <Select 
                                    value={filterType} 
                                    onChange={(e) => setFilterType(e.target.value)} 
                                    label="Type"
                                    sx={{ fontSize: '0.8rem', height: '32px' }}
                                >
                                    <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>All</MenuItem>
                                    <MenuItem value="csv" sx={{ fontSize: '0.8rem' }}>CSV</MenuItem>
                                    <MenuItem value="json" sx={{ fontSize: '0.8rem' }}>JSON</MenuItem>
                                    <MenuItem value="py" sx={{ fontSize: '0.8rem' }}>Python</MenuItem>
                                    <MenuItem value="ipynb" sx={{ fontSize: '0.8rem' }}>Notebook</MenuItem>
                                    <MenuItem value="jpg" sx={{ fontSize: '0.8rem' }}>Images</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                                <InputLabel sx={{ fontSize: '0.8rem' }}>Sort</InputLabel>
                                <Select 
                                    value={sortBy} 
                                    onChange={(e) => setSortBy(e.target.value)} 
                                    label="Sort"
                                    sx={{ fontSize: '0.8rem', height: '32px' }}
                                >
                                    <MenuItem value="name" sx={{ fontSize: '0.8rem' }}>Name</MenuItem>
                                    <MenuItem value="size" sx={{ fontSize: '0.8rem' }}>Size</MenuItem>
                                    <MenuItem value="date" sx={{ fontSize: '0.8rem' }}>Date</MenuItem>
                                    <MenuItem value="type" sx={{ fontSize: '0.8rem' }}>Type</MenuItem>
                                </Select>
                            </FormControl>
                            <IconButton>
                                <RefreshIcon />
                            </IconButton>
                        </Box>

                        {/* Files List */}
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Size</TableCell>
                                        <TableCell>Project</TableCell>
                                        <TableCell>Uploaded</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {storageData.recentFiles.map((file) => (
                                        <TableRow key={file.id} hover>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: getFileColor(file.type) + '.main' }}>
                                                        {getFileIcon(file.type)}
                                                    </Avatar>
                                                    <Typography variant="body2">{file.name}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={file.type.toUpperCase()} size="small" />
                                            </TableCell>
                                            <TableCell>{file.size}</TableCell>
                                            <TableCell>
                                                {projectsData.projects.find((p) => p.id === file.project)?.name || 'Unknown'}
                                            </TableCell>
                                            <TableCell>{formatDate(file.uploadedAt)}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <IconButton size="small" color="primary">
                                                        <ViewIcon />
                                                    </IconButton>
                                                    <IconButton size="small" color="secondary">
                                                        <DownloadIcon />
                                                    </IconButton>
                                                    <IconButton size="small" color="error">
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </TabPanel>

                    <TabPanel value={selectedTab} index={1}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 1.5 }}>
                            Recently Uploaded Files
                        </Typography>
                        <List>
                            {storageData.recentFiles.map((file, index) => (
                                <React.Fragment key={file.id}>
                                    <ListItem>
                                        <ListItemAvatar>
                                            <Avatar sx={{ bgcolor: getFileColor(file.type) + '.main' }}>{getFileIcon(file.type)}</Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={file.name}
                                            secondary={`${file.size} • ${formatDate(file.uploadedAt)} • ${file.path}`}
                                        />
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <IconButton size="small" color="primary">
                                                <ViewIcon />
                                            </IconButton>
                                            <IconButton size="small" color="secondary">
                                                <DownloadIcon />
                                            </IconButton>
                                        </Box>
                                    </ListItem>
                                    {index < storageData.recentFiles.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    </TabPanel>

                    <TabPanel value={selectedTab} index={2}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 1.5 }}>
                            Storage Analytics
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                            Storage Usage Trend
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            Last 30 days
                                        </Typography>
                                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Chart placeholder - Would show storage usage trend
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                            File Type Distribution
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            By file types
                                        </Typography>
                                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Chart placeholder - Would show file type distribution
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </TabPanel>
                    </Box>
                </CardContent>
            </Card>

            {/* Create Folder Dialog */}
            <Dialog open={openCreateFolderDialog} onClose={() => setOpenCreateFolderDialog(false)}>
                <DialogTitle>Create New Folder</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Folder Name"
                        fullWidth
                        variant="outlined"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCreateFolderDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateFolder} variant="contained">
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Upload Files Dialog */}
            <Dialog open={openUploadDialog} onClose={() => setOpenUploadDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Upload Files</DialogTitle>
                <DialogContent>
                    <Box sx={{ border: '2px dashed grey.300', borderRadius: 2, p: 4, textAlign: 'center' }}>
                        <UploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            Drag and drop files here
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            or click to browse files
                        </Typography>
                        <Button variant="outlined" sx={{ mt: 2 }}>
                            Choose Files
                        </Button>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Upload to Project</InputLabel>
                            <Select label="Upload to Project">
                                {projectsData.projects.map((project) => (
                                    <MenuItem key={project.id} value={project.id}>
                                        {project.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenUploadDialog(false)}>Cancel</Button>
                    <Button onClick={handleUpload} variant="contained">
                        Upload
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default StorageManagement;
